import { useEffect, useRef, useState } from "react";

/**
 * 36 px circular avatar. Renders the user's uploaded image when present,
 * otherwise two-letter initials over a lime gradient. Wrapped in a button
 * suitable for popover triggers.
 */
export function AccountAvatar({
  name,
  email,
  image,
  open,
  onClick,
}: {
  name: string | null;
  email: string;
  image: string | null;
  open?: boolean;
  onClick?: () => void;
}) {
  const initials = computeInitials(name, email);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Konto"
      aria-haspopup="menu"
      aria-expanded={open}
      className="grid size-12 place-items-center overflow-hidden rounded-full border transition-colors duration-fast"
      style={{
        borderColor: open ? "rgba(151,252,0,0.55)" : "rgba(255,255,255,0.10)",
        background: image
          ? "transparent"
          : "linear-gradient(140deg, rgba(151,252,0,0.55) 0%, rgba(151,252,0,0.18) 100%)",
        color: "#1d3700",
        boxShadow: open
          ? "0 0 0 2px rgba(151,252,0,0.18) inset"
          : "inset 0 1px 0 rgba(255,255,255,0.18)",
      }}
    >
      {image ? (
        <img src={image} alt="" className="size-full object-cover" />
      ) : (
        <span
          className="home-display"
          style={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.4 }}
        >
          {initials}
        </span>
      )}
    </button>
  );
}

function computeInitials(name: string | null, email: string): string {
  const source = (name && name.trim()) || email.split("@")[0] || "?";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return (parts[0] ?? "?").slice(0, 2).toUpperCase();
}

/**
 * Hook: attach click-outside + Escape handlers while `open` is true.
 * Used by every header popover in the app.
 */
export function useDismissPopover(
  open: boolean,
  onClose: () => void,
): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  // Cast: useRef<T>(null) is the canonical pattern for DOM refs even though
  // its inferred type is `RefObject<T | null>`. Consumers attach via ref={...}.
  return ref as React.RefObject<HTMLDivElement>;
}

/**
 * Reusable popover state hook — keeps the open/setOpen pair + an event-safe
 * close callback in one place so call sites stay tidy.
 */
export function usePopover(): {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
  close: () => void;
} {
  const [open, setOpen] = useState(false);
  return {
    open,
    setOpen,
    toggle: () => setOpen((v) => !v),
    close: () => setOpen(false),
  };
}
